// TES-1676 — what the "Utskick" page shows and decides, kept free of React
// so it can be unit-tested with plain `node --test`.
import {
  ANNOUNCEMENT_LIMITS,
  type AnnouncementBuildRange,
  type AnnouncementCampaignDraft,
  type AnnouncementCampaignOutcome,
  type AnnouncementCampaignSegment,
  type AnnouncementCampaignView,
  type AnnouncementCopyField,
  type AnnouncementSegmentCounts,
  missingAnnouncementCopy,
} from "@care-by-ella/contracts";

export const SEGMENT_LABEL: Record<AnnouncementCampaignSegment, string> = {
  f01_not_done: "Inloggade i appen, F-01 inte gjord",
  f01_done_not_booked: "F-01 gjord, inget vårdmöte bokat",
};

export const COPY_FIELD_LABEL: Record<AnnouncementCopyField, string> = {
  pushTitle: "Pushrubrik",
  pushBody: "Pushtext",
  cardTitle: "Rubrik på Hem-kortet",
  cardBody: "Text på Hem-kortet",
  buttonLabel: "Knapptext",
};

// Why a patient in the segment is not reached, in the order the page lists
// the counts. `older_build` is the box script's bucket only: an admin
// campaign reaches every build, so it is always 0 here and not shown.
export const HELD_LABEL: Record<
  Exclude<keyof AnnouncementSegmentCounts, "eligible" | "older_build">,
  string
> = {
  cannot_book:
    "Kan inte boka vårdmöte nu (under 18, akut signal, receptövertag m.m.)",
  f01_stopped: "F-01 gav stopp (Resultat B), undantagna",
  opted_out: "Har stängt av utskick",
  no_push_token: "Saknar pushbehörighet",
  outside_build: "Appversion utanför urvalet",
  unknown_build: "Okänd appversion (räknas bort när ett urval är satt)",
  test: "Testpatienter",
  transfer: "Receptövertag (F-07), undantagna",
};

// TES-1687 — a sent campaign's outcome, in the page's order: sent, opened,
// free meetings opened, booked.
export function outcomeRows(
  o: AnnouncementCampaignOutcome,
): { label: string; value: number }[] {
  return [
    { label: "Skickade", value: o.sent },
    { label: "Öppnade", value: o.opened },
    { label: "Kostnadsfritt vårdmöte öppnat", value: o.freeMeetings },
    { label: "Bokade", value: o.booked },
  ];
}

// "Öppnade" counts only apps that report a tap, so it is a floor. Says how
// many of the sent pushes went to such an app.
export function openedFloorText(o: AnnouncementCampaignOutcome): string {
  if (o.sent === 0) return "";
  if (o.reportingTaps === 0) {
    return "Öppnade räknas bara i appversioner som rapporterar när en notis öppnas. Ingen mottagare har en sådan app ännu, så siffran säger inget om hur många som öppnade.";
  }
  if (o.reportingTaps >= o.sent) {
    return "Alla mottagare har en appversion som rapporterar när en notis öppnas.";
  }
  return `Öppnade räknas bara i appversioner som rapporterar när en notis öppnas. ${o.reportingTaps} av ${o.sent} mottagare har en sådan app, så det verkliga antalet kan vara högre.`;
}

/** "Alla versioner", "2.0.11 och äldre", "2.1.0 och nyare", "2.0.11–2.1.0". */
export function buildRangeText(d: AnnouncementBuildRange): string {
  const { minAppVersion: min, maxAppVersion: max } = d;
  if (min && max) return min === max ? `Endast ${min}` : `${min}–${max}`;
  if (min) return `${min} och nyare`;
  if (max) return `${max} och äldre`;
  return "Alla appversioner";
}

export function emptyDraft(): AnnouncementCampaignDraft {
  return {
    key: "",
    segment: "f01_done_not_booked",
    pushTitle: "",
    pushBody: "",
    cardTitle: "",
    cardBody: "",
    buttonLabel: "Boka ditt vårdmöte",
    freeMeetingUntil: null,
    minAppVersion: null,
    maxAppVersion: null,
  };
}

export function draftOf(
  c: AnnouncementCampaignView,
): AnnouncementCampaignDraft {
  return {
    key: c.key,
    segment: c.segment,
    pushTitle: c.pushTitle,
    pushBody: c.pushBody,
    cardTitle: c.cardTitle,
    cardBody: c.cardBody,
    buttonLabel: c.buttonLabel,
    freeMeetingUntil: c.freeMeetingUntil,
    minAppVersion: c.minAppVersion,
    maxAppVersion: c.maxAppVersion,
  };
}

/** The push body's length line: how much a locked iPhone shows. */
export function pushBodyHint(body: string): {
  text: string;
  tooLong: boolean;
} {
  const n = body.length;
  const visible = ANNOUNCEMENT_LIMITS.pushBodyVisible;
  return {
    text: `${n} av ${ANNOUNCEMENT_LIMITS.pushBody} tecken. Ungefär ${visible} syns i pushen på en iPhone.`,
    tooLong: n > visible,
  };
}

export type SendState = { canSend: true } | { canSend: false; reason: string };

/** Whether the send button may be pressed, and if not, why. */
export function sendState(
  saved: AnnouncementCampaignView | null,
  draft: AnnouncementCampaignDraft,
  confirmKey: string,
  todayIso: string,
): SendState {
  if (!saved) return { canSend: false, reason: "Spara utkastet först." };
  if (saved.status === "sent") {
    return { canSend: false, reason: "Utskicket är redan skickat." };
  }
  if (JSON.stringify(draftOf(saved)) !== JSON.stringify(draft)) {
    return { canSend: false, reason: "Spara ändringarna först." };
  }
  const missing = missingAnnouncementCopy(saved);
  if (missing.length > 0) {
    return {
      canSend: false,
      reason: `Fyll i ${missing.map((f) => COPY_FIELD_LABEL[f].toLowerCase()).join(", ")}.`,
    };
  }
  if (saved.freeMeetingUntil && saved.freeMeetingUntil < todayIso) {
    return {
      canSend: false,
      reason: "Datumet för kostnadsfritt vårdmöte har redan passerat.",
    };
  }
  if (confirmKey.trim() !== saved.key) {
    return {
      canSend: false,
      reason: `Skriv nyckeln ${saved.key} för att bekräfta.`,
    };
  }
  return { canSend: true };
}

const ERROR_TEXT: Record<string, string> = {
  validation_failed:
    "Utskicket kunde inte sparas. Nyckeln får bara innehålla små bokstäver, siffror och bindestreck, och texterna har en högsta längd.",
  announcement_campaign_key_in_use:
    "Nyckeln används redan av ett annat utskick.",
  announcement_campaign_already_sent:
    "Utskicket är redan skickat och kan inte ändras.",
  announcement_campaign_confirm_mismatch:
    "Skriv utskickets nyckel exakt för att skicka.",
  announcement_campaign_copy_missing: "Fyll i all text innan du skickar.",
  announcement_campaign_free_until_past:
    "Datumet för kostnadsfritt vårdmöte har redan passerat.",
  announcement_campaign_refused: "Utskicket kunde inte skickas.",
  announcement_campaign_not_found: "Utskicket kunde inte hittas.",
};

/** The page's line for an API refusal (the error code in `message`). */
export function campaignErrorText(code: string): string {
  return ERROR_TEXT[code] ?? "Något gick fel. Försök igen.";
}

/** "3 nov 2026" for a YYYY-MM-DD date. */
export function longDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
